import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Lottie from "lottie-react";
import claimAnim from "../../assets/lottie/claim_anim.json";
import { pushBackHandler, popBackHandler } from "../../utils/backHandler";

// Crypto position icons
import goldIcon from '../../assets/icon/cryptoposition/gold.png';
import silverIcon from '../../assets/icon/cryptoposition/silver.png';
import bronzeIcon from '../../assets/icon/cryptoposition/bronze.png';
import royalIcon from '../../assets/icon/cryptoposition/royal.png';
import emeraldIcon from '../../assets/icon/cryptoposition/emerald.png';
import devineIcon from '../../assets/icon/cryptoposition/devine.png';
import supremeIcon from '../../assets/icon/cryptoposition/supreme.png';
import { getCrystalRarityTiers } from '../../data/crystalRarityTiers';

const TIER_ICONS = {
    Bronze: bronzeIcon,
    Silver: silverIcon,
    Gold: goldIcon,
    Lucky: emeraldIcon,
    Royal: royalIcon,
    Nova: supremeIcon,
    Diamond: devineIcon,
};

const buildCryptoPositionIcons = (total) =>
    getCrystalRarityTiers(total).map((tier) => ({
        ...tier,
        icon: TIER_ICONS[tier.name],
    }));

const cryptoPositionIcons = {
    3: buildCryptoPositionIcons(3),
    5: buildCryptoPositionIcons(5),
    7: buildCryptoPositionIcons(7),
};

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII'];

// Card dimensions
const CARD_WIDTH = 110;
const CARD_GAP = 10;
const CARD_TOTAL = CARD_WIDTH + CARD_GAP;

/**
 * Single roulette card for a position
 */
function RouletteCard({ item, promoType, tokenIcon, isWinner, revealed }) {
    const isCryptoOnly = promoType === 'cryptoOnly';
    const isOnlyCrystals = promoType === 'onlyCrystals';

    return (
        <motion.div
            className="flex flex-col items-center justify-center relative flex-shrink-0"
            style={{
                width: CARD_WIDTH,
                height: 150,
                borderRadius: 14,
                background: isWinner && revealed
                    ? 'linear-gradient(to bottom, #4a3a10, #1a1a1a)'
                    : 'linear-gradient(to bottom, #2a2828, #111111)',
                border: isWinner && revealed ? '2px solid #FFC813' : '2px solid transparent',
                boxShadow: isWinner && revealed ? '0 0 20px rgba(255, 200, 19, 0.4)' : 'none',
                overflow: 'hidden',
            }}
            animate={isWinner && revealed ? { scale: [1, 1.08, 1.04] } : {}}
            transition={{ duration: 0.5, ease: 'easeOut' }}
        >
            {/* Roman numeral */}
            {!isCryptoOnly && (
                <span className="text-[#959595] text-[11px] mb-[2px]">{item.roman}</span>
            )}

            {/* Icon */}
            <div className="relative w-[50px] h-[50px] flex items-center justify-center">
                <img
                    src={item.icon}
                    alt={item.name}
                    className="w-full h-full object-contain"
                />
                {isCryptoOnly && (
                    <span
                        className="absolute inset-0 flex items-center justify-center font-bold text-[14px]"
                        style={{ color: item.color || '#EAEAEA', marginTop: '-6px' }}
                    >
                        {item.roman}
                    </span>
                )}
            </div>

            {/* Name */}
            <span className="text-white text-[12px] font-semibold mt-[4px] text-center leading-tight px-1">
                {item.name} reward
            </span>

            {/* Token reward (not for onlyCrystals) */}
            {!isOnlyCrystals && item.reward > 0 && (
                <div className="flex flex-row items-center gap-[4px] mt-[3px]">
                    {tokenIcon && (
                        <img src={tokenIcon} className="w-[14px] h-[14px] rounded-full" alt="token" />
                    )}
                    <span className="text-[#DEC315] text-[11px] font-semibold">
                        {formatReward(item.reward)}
                    </span>
                </div>
            )}

            {/* Chance */}
            <span className="text-[#959595] text-[10px] mt-[2px]">
                {item.chance?.toFixed(2) || '0.00'} % chance
            </span>
        </motion.div>
    );
}

function formatReward(val) {
    if (!val) return '0';
    if (val >= 1_000_000) return (val / 1_000_000).toFixed(1) + 'M';
    if (val >= 1_000) return (val / 1_000).toFixed(1) + 'K';
    return val.toFixed(val < 1 ? 4 : 2);
}

/**
 * Main ClaimRoulette component
 *
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - positions: array from promo.category.positions
 * - crystals: array of gem objects (for onlyCrystals / crystalsAndCrypto)
 * - promoType: 'onlyCrystals' | 'cryptoOnly' | 'crystalsAndCrypto'
 * - tokenIcon: string (URL of token logo)
 * - tokenTicker: string
 * - winnerPosition: number (1-based position from award response)
 * - winnerAmount: number
 */
export default function ClaimRoulette({
    isOpen,
    onClose,
    positions = [],
    crystals = [],
    promoType = 'crystalsAndCrypto',
    tokenIcon = '',
    tokenTicker = '',
    winnerPosition = 1,
    winnerAmount = 0,
}) {
    const [phase, setPhase] = useState('spinning'); // 'spinning' | 'revealed' | 'celebrate'
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(360);

    // Перехват Telegram BackButton — закрыть рулетку при нажатии "назад"
    const handleCloseRoulette = useCallback(() => {
        if (onClose) onClose();
    }, [onClose]);

    useEffect(() => {
        if (!isOpen) return;
        pushBackHandler(handleCloseRoulette);
        return () => popBackHandler(handleCloseRoulette);
    }, [isOpen, handleCloseRoulette]);

    // Measure container width
    useEffect(() => {
        if (isOpen && containerRef.current) {
            setContainerWidth(containerRef.current.offsetWidth);
        }
    }, [isOpen]);

    // Build the items array from positions
    const items = useMemo(() => {
        const isCryptoOnly = promoType === 'cryptoOnly';
        const posCount = positions.length || 3;
        const iconMap = cryptoPositionIcons[posCount] || cryptoPositionIcons[3];

        // Build gems map
        const gemsMap = {};
        crystals.forEach(gem => {
            if (gem.position) gemsMap[gem.position] = gem;
        });

        return positions.map((pos, idx) => {
            const gemPos = posCount - idx; // gem position mapping
            const gem = gemsMap[gemPos];
            const cryptoIcon = isCryptoOnly ? iconMap[idx] : null;

            let icon, name, color;
            if (isCryptoOnly && cryptoIcon) {
                icon = cryptoIcon.icon;
                name = cryptoIcon.name;
                color = cryptoIcon.color;
            } else if (gem?.frontImage) {
                icon = `https://api.duotetra.quest/uploads/gems/${gem.frontImage}`;
                name = gem.name || 'Crystal';
                color = '#959595';
            } else {
                icon = '';
                name = `Position ${idx + 1}`;
                color = '#959595';
            }

            // Chance: winners / total participants (approximate)
            const totalWinners = positions.reduce((sum, p) => sum + (p.winners || 0), 0);
            const chance = totalWinners > 0
                ? ((pos.winners || 0) / totalWinners) * 100
                : 0;

            return {
                idx,
                positionNumber: posCount - idx, // 1-based position for matching with winnerPosition
                roman: ROMAN[idx] || `${idx + 1}`,
                icon,
                name,
                color,
                reward: pos.dropPerWinner || 0,
                winners: pos.winners || 0,
                chance,
            };
        });
    }, [positions, crystals, promoType]);

    // Find the winner index in items array
    const winnerIdx = useMemo(() => {
        const found = items.findIndex(item => item.positionNumber === winnerPosition);
        return found >= 0 ? found : 0;
    }, [items, winnerPosition]);

    const winnerItem = items[winnerIdx] || items[0];

    // Build repeated strip for roulette (repeat items 6 times for longer spin, winner in 5th cycle)
    const REPEATS = 6;
    const stripItems = useMemo(() => {
        const strip = [];
        for (let r = 0; r < REPEATS; r++) {
            items.forEach((item, i) => {
                strip.push({ ...item, stripIdx: r * items.length + i, repeat: r });
            });
        }
        return strip;
    }, [items]);

    // Calculate target X position to land winner under center indicator
    // Winner card should be in the 5th repeat (index 4) — further away for longer spin
    const targetStripIdx = 4 * items.length + winnerIdx;
    const targetX = useMemo(() => {
        const cardCenter = targetStripIdx * CARD_TOTAL + CARD_WIDTH / 2;
        const viewCenter = containerWidth / 2;
        return -(cardCenter - viewCenter);
    }, [targetStripIdx, containerWidth]);

    // Animation duration: ~5 sec total (3s fast spin + 2s deceleration)
    const SPIN_DURATION = 5;

    // Reset state when opening
    useEffect(() => {
        if (isOpen) {
            setPhase('spinning');
            // After animation completes, reveal
            const timer = setTimeout(() => setPhase('revealed'), SPIN_DURATION * 1000 + 300);
            const celebrateTimer = setTimeout(() => setPhase('celebrate'), SPIN_DURATION * 1000 + 1000);
            return () => {
                clearTimeout(timer);
                clearTimeout(celebrateTimer);
            };
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const stripWidth = stripItems.length * CARD_TOTAL;

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="fixed inset-0 z-50 flex flex-col items-center justify-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    {/* Backdrop */}
                    <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" />

                    {/* Content */}
                    <div className="relative z-10 w-full flex flex-col items-center px-4">
                        {/* Title */}
                        <motion.div
                            className="mb-6"
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2, duration: 0.4 }}
                        >
                            <span className="text-white text-[20px] font-bold">
                                {phase === 'celebrate' ? 'You won!' : 'Opening reward...'}
                            </span>
                        </motion.div>

                        {/* Roulette container */}
                        <div
                            ref={containerRef}
                            className="relative w-full overflow-hidden"
                            style={{ height: 170 }}
                        >
                            {/* Center indicator line */}
                            <div
                                className="absolute top-0 bottom-0 z-20"
                                style={{
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: 3,
                                    background: 'linear-gradient(to bottom, transparent, #FFC813, transparent)',
                                }}
                            />
                            {/* Center indicator arrows */}
                            <div
                                className="absolute z-20"
                                style={{
                                    left: '50%',
                                    top: -2,
                                    transform: 'translateX(-50%)',
                                    width: 0,
                                    height: 0,
                                    borderLeft: '8px solid transparent',
                                    borderRight: '8px solid transparent',
                                    borderTop: '10px solid #FFC813',
                                }}
                            />
                            <div
                                className="absolute z-20"
                                style={{
                                    left: '50%',
                                    bottom: -2,
                                    transform: 'translateX(-50%)',
                                    width: 0,
                                    height: 0,
                                    borderLeft: '8px solid transparent',
                                    borderRight: '8px solid transparent',
                                    borderBottom: '10px solid #FFC813',
                                }}
                            />

                            {/* Fade edges */}
                            <div className="absolute top-0 bottom-0 left-0 w-[40px] z-10"
                                style={{ background: 'linear-gradient(to right, rgba(0,0,0,0.9), transparent)' }}
                            />
                            <div className="absolute top-0 bottom-0 right-0 w-[40px] z-10"
                                style={{ background: 'linear-gradient(to left, rgba(0,0,0,0.9), transparent)' }}
                            />

                            {/* Scrolling strip */}
                            <motion.div
                                className="flex flex-row items-center absolute top-[10px]"
                                style={{ gap: CARD_GAP, width: stripWidth }}
                                initial={{ x: 0 }}
                                animate={{ x: targetX }}
                                transition={{
                                    duration: SPIN_DURATION,
                                    ease: [0.05, 0.35, 0.15, 1.0], // Fast start, long spin, smooth deceleration
                                }}
                            >
                                {stripItems.map((item, i) => {
                                    const isTarget = i === targetStripIdx;
                                    return (
                                        <RouletteCard
                                            key={i}
                                            item={item}
                                            promoType={promoType}
                                            tokenIcon={tokenIcon}
                                            isWinner={isTarget}
                                            revealed={phase === 'revealed' || phase === 'celebrate'}
                                        />
                                    );
                                })}
                            </motion.div>
                        </div>

                        {/* Celebration section */}
                        <AnimatePresence>
                            {phase === 'celebrate' && (
                                <motion.div
                                    className="flex flex-col items-center mt-6 relative"
                                    initial={{ opacity: 0, scale: 0.5 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                                >
                                    {/* Lottie celebration */}
                                    <div className="absolute -top-[80px] w-[250px] h-[250px] pointer-events-none">
                                        <Lottie
                                            animationData={claimAnim}
                                            loop={false}
                                            style={{ width: '100%', height: '100%' }}
                                        />
                                    </div>

                                    {/* Winner card enlarged */}
                                    <div
                                        className="flex flex-col items-center p-4 rounded-[16px] relative z-10"
                                        style={{
                                            background: 'linear-gradient(to bottom, #3a3000, #1a1a1a)',
                                            border: '2px solid #FFC813',
                                            boxShadow: '0 0 30px rgba(255, 200, 19, 0.3)',
                                            minWidth: 180,
                                        }}
                                    >
                                        <img
                                            src={winnerItem?.icon}
                                            alt={winnerItem?.name}
                                            className="w-[70px] h-[70px] object-contain"
                                        />
                                        <span className="text-white text-[18px] font-bold mt-2">
                                            {winnerItem?.name} reward
                                        </span>

                                        {promoType !== 'onlyCrystals' && winnerAmount > 0 && (
                                            <div className="flex flex-row items-center gap-2 mt-2 px-4 py-2 rounded-full"
                                                style={{ border: '1.5px solid #5BC2E7' }}
                                            >
                                                {tokenIcon && (
                                                    <img src={tokenIcon} className="w-[20px] h-[20px] rounded-full" alt="token" />
                                                )}
                                                <span className="text-[#FFC813] text-[16px] font-bold">
                                                    {formatReward(winnerAmount)}
                                                </span>
                                                {tokenTicker && (
                                                    <span className="text-white text-[14px] font-semibold">
                                                        {tokenTicker}
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        {promoType === 'onlyCrystals' && (
                                            <div className="flex flex-row items-center gap-2 mt-2">
                                                <span className="text-[#FFC813] text-[14px] font-bold">
                                                    {winnerAmount > 0 ? formatReward(winnerAmount) : ''} crystals
                                                </span>
                                            </div>
                                        )}

                                        <span className="text-[#959595] text-[11px] mt-1">
                                            {winnerItem?.chance?.toFixed(2) || '0.00'} % chance
                                        </span>
                                    </div>

                                    {/* Close button */}
                                    <motion.div
                                        className="mt-6 cursor-pointer"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.5, duration: 0.4 }}
                                        onClick={onClose}
                                    >
                                        <div className="px-8 py-3 bg-[#FFC813] rounded-[12px]">
                                            <span className="text-black text-[16px] font-bold">Awesome!</span>
                                        </div>
                                    </motion.div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
